import { useHistory, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useSignUpClient } from "@/hooks/tenancy/use-signup-client";
import { toast } from "react-toastify";
import { AuthLayout } from "./auth-layout";
import { getClientRegistrationData } from "@/api/v1-jwt/tenancy/tenancy";
import { useAuth } from "@/providers/auth";
import { ClientSignUpSchema } from "./schema/client-signup-schema";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import { useClientRegistration } from "@/hooks/tenancy/use-client-registration";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

export function ClientSignUpPage() {
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const registrationId = queryParams.get("id");

  if (!registrationId) {
    return history.replace("/client/auth/support");
  }

  return (
    <AuthLayout>
      <div className="mx-auto w-full max-w-lg">
        <div>
          <h2 className="mt-8 text-3xl font-bold leading-9 tracking-tight text-gray-900">
            Save money. Run your business.
          </h2>
        </div>

        <div className="mt-10">
          <SignUpForm id={registrationId} />
        </div>
      </div>
    </AuthLayout>
  );
}

const makeSteps = (password) => [
  { id: "A-Z", name: "Uppercase", completed: /[A-Z]+/.test(password) },
  { id: "a-z", name: "Lowercase", completed: /[a-z]+/.test(password) },
  { id: "0-9", name: "Number", completed: /[0-9]+/.test(password) },
  {
    id: "/*+&...",
    name: "Special",
    completed: /[$&+,:;=?@#|'<>.^*()%!-]+/.test(password),
  },
  { id: "+12 char", name: "Chars", completed: password.length >= 12 },
];

function PasswordValidation({ password }) {
  if (typeof password !== "string") {
    throw new Error(
      "[PASSWORD VALIDATION COMPONENT]::Password must be a string value."
    );
  }

  const steps = makeSteps(password);

  return (
    <nav aria-label="Progress">
      <ol className="space-y-4 md:flex md:space-x-4 md:space-y-0">
        {steps.map((step) => (
          <li key={step.name} className="md:flex-1">
            {step.completed ? (
              <span className="group flex flex-col border-l-4 border-emerald-600 py-2 pl-4 hover:border-emerald-800 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
                <span className="text-sm font-medium text-emerald-600 group-hover:text-emerald-800">
                  {step.id}
                </span>
              </span>
            ) : (
              <span className="group flex flex-col border-l-4 border-gray-200 py-2 pl-4 hover:border-gray-300 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
                <span className="text-sm font-medium text-gray-500 group-hover:text-gray-700">
                  {step.id}
                </span>
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function phoneNumberMask(phone) {
  const USPhone = (phone || "")
    .replace(/\D/g, "")
    .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  return !USPhone[2]
    ? USPhone[1]
    : "(" +
        USPhone[1] +
        ") " +
        USPhone[2] +
        (USPhone[3] ? "-" + USPhone[3] : "");
}

function SignUpForm({ id }) {
  const [userId, setUserId] = useState(null);
  const history = useHistory();
  const { signin } = useAuth();
  const { mutate: signUp, isLoading: isSigningUp } = useSignUpClient();
  const { data: tenancy } = useTenancy({ userId, enabled: !!userId });
  const {
    data: clientRegistration,
    isError: registrationDataError,
    isLoading: registrationDataLoading,
  } = useClientRegistration({
    registrationId: id,
    onSuccess: (data) => {
      setUserId(data.user_id);
    },
  });
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(ClientSignUpSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      terms: false,
    },
  });

  useEffect(() => {
    if (clientRegistration) {
      reset({
        name: clientRegistration?.business_name || "",
        phone: clientRegistration?.phone_number || "",
        email: clientRegistration?.email_address || "",
      });
    }
  }, [clientRegistration, reset]);

  useEffect(() => {
    if (!registrationDataLoading && !clientRegistration?.email_address) {
      history.push("/client/auth/support");
    }
  }, [clientRegistration, registrationDataError]);

  const password = watch("password");

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const handleLogin = async ({ email, password }) => {
    const payload = {
      username: email,
      password,
    };

    await signin(payload, {
      onSuccess: async () => {
        history.replace("/dashboard");
      },
      onError: (error) => {
        console.log("[LOGIN ERROR]::::::: ", error);
        toast.error("Failed to login after sign up. Please try again.");
        history.replace("/login");
      },
    });
  };

  const onSubmit = ({ name, email, phone, password, terms }) => {
    let payload = {
      user_id: userId,
      subdomain: tenancy?.subdomain,
      business_name: name.trim(),
      business_phone: phone,
      business_email: email,
      password: password,
      accepted_pb_tos_pp: terms,
      user_type: "client",
    };

    signUp(payload, {
      onSuccess: () => {
        handleLogin({ email, password });
      },
      onError: (error) => {
        console.log("ERROR_CLIENT_SIGNUP::", error);
        toast.error("Something went wrong.");
      },
    });
  };

  return registrationDataError ? (
    <div className="text-red-700 font-medium text-lg flex items-center">
      <ExclamationCircleIcon className="h-5 w-5 mr-4" />
      Failed registration check. Please try again.
    </div>
  ) : (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Business email
        </label>
        <div className="relative mt-2">
          <input
            {...register("email")}
            id="email"
            type="email"
            autoComplete="email"
            className="peer block w-full border-0 bg-gray-50 py-1.5 text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
          />
          <div
            className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
            aria-hidden="true"
          />
        </div>
        {errors.email && (
          <p className="mt-2 text-sm text-red-600" id="email-error">
            {errors.email.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Business name
        </label>
        <div className="relative mt-2">
          <input
            {...register("name")}
            id="name"
            type="text"
            className="peer block w-full border-0 bg-gray-50 py-1.5 text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
          />
          <div
            className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
            aria-hidden="true"
          />
        </div>
        {errors.name && (
          <p className="mt-2 text-sm text-red-600" id="name-error">
            {errors.name.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Business phone
        </label>
        <div className="relative mt-2">
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <input
                {...field}
                value={phoneNumberMask(field.value)}
                onChange={(e) =>
                  field.onChange(
                    e.target.value.replace(/\D/g, "").substring(0, 10)
                  )
                }
                id="phone"
                type="text"
                autoComplete="phone"
                className="peer block w-full border-0 bg-gray-50 py-1.5 text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
              />
            )}
          />
          <div
            className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
            aria-hidden="true"
          />
        </div>
        {errors.phone && (
          <p className="mt-2 text-sm text-red-600" id="phone-error">
            {errors.phone.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Password
        </label>
        <div className="relative mt-2">
          <input
            {...register("password")}
            id="password"
            type={passwordVisible ? "text" : "password"}
            className="peer block w-full border-0 bg-gray-50 py-1.5 text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
          />
          <div
            className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
            aria-hidden="true"
          />
          <div
            className=" absolute hover:cursor-pointer transition-all inset-y-0 right-0 flex gap-x-2 items-center pr-3"
            onClick={() => setPasswordVisible((prev) => !prev)}
          >
            {passwordVisible ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </div>
        </div>
      </div>
      {password && <PasswordValidation password={password} />}

      <div>
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          Confirm password
        </label>
        <div className="relative mt-2">
          <input
            {...register("passwordConfirmation")}
            id="passwordConfirmation"
            type={confirmPasswordVisible ? "text" : "password"}
            className="peer block w-full border-0 bg-gray-50 py-1.5 text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
          />
          <div
            className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
            aria-hidden="true"
          />
          <div
            className=" absolute hover:cursor-pointer transition-all inset-y-0 right-0 flex gap-x-2 items-center pr-3"
            onClick={() => setConfirmPasswordVisible((prev) => !prev)}
          >
            {confirmPasswordVisible ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
            {/* <XCircle className="h-5 w-5 text-red-500" aria-hidden="true" /> */}
          </div>
        </div>
        {errors.passwordConfirmation && (
          <p
            className="mt-2 text-sm text-red-600"
            id="passwordConfirmation-error"
          >
            {errors.passwordConfirmation.message}
          </p>
        )}
      </div>

      <div className="flex flex-col justify-between">
        <div className="flex items-center">
          <input
            {...register("terms")}
            id="terms"
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
          />
          <label
            htmlFor="remember-me"
            className="ml-3 block text-sm leading-6 text-gray-700"
          >
            I agree to Pocketbook's{" "}
            <a
              href="https://vault.pactsafe.io/s/baa76388-6efd-4b54-b2c2-ff6cd8ab2be9/legal#terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50  text-slate-800 underline-offset-4 hover:underline "
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://vault.pactsafe.io/s/baa76388-6efd-4b54-b2c2-ff6cd8ab2be9/legal#privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50  text-slate-800 underline-offset-4 hover:underline "
            >
              Privacy Policy
            </a>
          </label>
        </div>
        {errors.terms && (
          <p
            className="mt-2 text-sm text-red-600"
            id="passwordConfirmation-error"
          >
            {errors.terms.message}
          </p>
        )}
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting || isSigningUp}
          className="flex w-full justify-center rounded-lg bg-black px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-black/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
        >
          Sign up
          {(isSubmitting || isSigningUp) && (
            <Loader2 className="ml-2 h-4 w-4 animate-spin" />
          )}
        </button>
      </div>
    </form>
  );
}

export default ClientSignUpPage;
